import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  DialogActions,
  Divider,
  Typography,
  FormControl,
  InputLabel
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { getTomorrowDate, getDateFormat } from '../../utils/dates';
import {
  STATUS,
  DEFAULT_OBJECTIVE_WEIGHT,
  ERROR_MESSAGES
} from '../../utils/const.js';
import { validateInput, validateAllInputs } from '../../utils/validateObjective';

import CategorySelect from './CategorySelect';
import CustomDatePicker from '../DatePicker/CustomDatePicker';

import { RATING_ROLE } from '../../utils/const';
import { useMutation } from 'react-query';
import { createObjective, updateObjective } from '../../api/objective/objective.api';
import { addAlert } from '../../actions/alerts.action';

const NO_CATEGORIES = [];

const ObjectiveDialog = ({
  open,
  handleClose,
  objectiveItem,
  employeeIdOfCurrentUser ,
  onSubmitSuccess
}) => {
  const dispatch = useDispatch();
  const categories = useSelector(state => state.accountInfo.categories ?? NO_CATEGORIES);
  const possibleAppraisers =  useSelector(state => state.accountInfo.possibleAppraisers);

  const getLeaderAppraiser = (objectiveItem, possibleAppraisers) => {
    if (!possibleAppraisers) {
      return {};
    }

    const appraiser = possibleAppraisers.find(appraiser => {
      const leaderEmployeeId = objectiveItem.appraisers_and_criterias.appraisers
        .find(appraiser => appraiser.role === RATING_ROLE.LEADER).employee_id;

      return appraiser.empid === leaderEmployeeId;
    });

    return appraiser;
  };

  const [ objective, setObjective ] = useState(objectiveItem ? {
    ...objectiveItem,
    appraiser: getLeaderAppraiser(objectiveItem, possibleAppraisers)
  } : {
    name: '',
    description: '',
    deadline: getTomorrowDate(),
    weight: DEFAULT_OBJECTIVE_WEIGHT,
    category: '',
    appraiser: {},
    status: STATUS.PROPOSED
  });

  useEffect(() => {
    if (objectiveItem) {
      setObjective(objective => {
        return {
          ...objectiveItem,
          appraiser: getLeaderAppraiser(objectiveItem, possibleAppraisers),
          category: categories.find(
            category => category.category_name.hu === objectiveItem.category
          )
        };
      });
    }
  }, [ categories, objectiveItem, possibleAppraisers ]);

  const { t } = useTranslation();

  const [ validation, setValidation ] = useState(
    { name: false, description: false, deadline: false, appraiser: false, category: false }
  );

  const resetState = () => {
    setValidation({ name: false, description: false, deadline: false, category: false });
    setObjective({
      name: '',
      description: '',
      deadline: getTomorrowDate(),
      weight: DEFAULT_OBJECTIVE_WEIGHT,
      category: '',
      appraiser: '',
      status: STATUS.PROPOSED
    });
  };

  const saveObjectiveMutation = useMutation(
    async () => {
      const newObjective = {
        ...objective,
        appraiserId: objective.appraiser.empid
      };

      return objective.id ?
        await updateObjective({ objective: newObjective }) :
        await createObjective({ objective: newObjective });
    }, {
      onSuccess: () => {
        onSubmitSuccess?.();
        resetState();
        handleClose();
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const handleObjectiveSave = () => {
    if (!validateAllInputs(setValidation, objective)) {
      return;
    }

    saveObjectiveMutation.mutate();
  };

  const handleDialogClose = () => {
    if (!objective.objective_id) {
      resetState();
    }
    handleClose();
  };

  const handleOnChangeName = event => {
    setObjective({
      ...objective,
      name: event.target.value
    });
    validateInput(setValidation, validation, 'name', event.target.value);
  };

  const handleOnChangeDescription = event => {
    setObjective({
      ...objective,
      description: event.target.value
    });
    validateInput(setValidation, validation,'description', event.target.value);
  };

  const handleOnChangeAppraiser = (event, values) => {
    setObjective({
      ...objective,
      appraiser: values
    });
    validateInput(setValidation, validation, 'appraiser', values);
  };

  const handleDateChange = date => {
    setObjective({
      ...objective,
      deadline: date
    });
    validateInput(setValidation, validation,'deadline', date);
  };

  const handleOnChangeWeight = event => {
    let { value, min, max } = event.target;
    value = Math.max(min, Math.min(max, value));
    setObjective({
      ...objective,
      weight: value
    });
  };

  const handleOnChangeCategory = event => {
    setObjective({
      ...objective,
      category: event.target.value
    });
    validateInput(setValidation, validation, 'category', event.target.value);
  };

  const useStyles = makeStyles(theme => ({
    dialogDivider: {
      marginBottom: '7px'
    },
    inputLabel: {
      marginTop: '10px'
    },
    containerForSmall: {
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    datepicker: {
      minWidth: '170px'
    }
  }));

  const classes = useStyles();

  return (
    <Dialog
      PaperProps={ {
        style: {
          minHeight: '600px', // or whatever ensures calendar fits
          overflow: 'visible' // allow popper to extend beyond dialog
        }
      } }
      open={ open }
      fullWidth={ true }
      maxWidth={ 'md' }
      onClose={ handleClose }
    >
      <DialogTitle>{ t('objective.dialog.create.title') }</DialogTitle>
      <DialogContent style={ { overflow: 'visible' } }>
        <Grid container direction={ 'column' } justifyContent={ 'center' } alignItems={ 'stretch' }>
          <InputLabel>
            { t('objective.dialog.create.name.label') }
          </InputLabel>
          <TextField
            value={ objective.name }
            error={ validation.name }
            helperText={ validation.name ? t(ERROR_MESSAGES.NAME) : ' ' }
            onChange={ handleOnChangeName }
            fullWidth={ true }
            placeholder={ t('objective.dialog.create.name.placeholder') }
          >
            <Typography variant='subtitle2'></Typography>
          </TextField>
          <InputLabel  className={ classes.inputLabel }>
            { t('objective.dialog.create.description.label') }
          </InputLabel>
          <TextField
            value={ objective.description }
            error={ validation.description }
            helperText={ validation.description ? t(ERROR_MESSAGES.DESCRIPTION) : '' }
            multiline
            minRows={ 3 }
            maxRows={ 3 }
            onChange={ handleOnChangeDescription }
            fullWidth={ true }
            placeholder={ t('objective.dialog.create.description.placeholder') }
          />
          { (!objective.objective_id || objective.proposed_by === employeeIdOfCurrentUser) &&
          <>
            <InputLabel  className={ classes.inputLabel }>
              { t('objective.dialog.create.appraiser.label') }
            </InputLabel>
            <Autocomplete
              value={ objective.objective_id ? objective.appraiser : undefined }
              options={ possibleAppraisers }
              getOptionLabel={ option => `${option.name} (${option.address})` }
              style={ { maxWidth: 300 } }
              renderInput={ params => <TextField
                { ...params }
                error={ validation.appraiser }
                helperText={ validation.appraiser ? t(ERROR_MESSAGES.APPRAISER) : '' }
                placeholder={ t('objective.dialog.create.appraiser.placeholder') }
              /> }
              onChange={ handleOnChangeAppraiser }
            />
          </>
          }
          <Divider className={ classes.dialogDivider } flexItem={ true }/>
          <Grid container spacing={ 2 } className ={ classes.containerForSmall }>
            <Grid item xs={ 4 } className = { classes.datepicker }>
              <CustomDatePicker
                inputLabel={ t('objective.dialog.create.deadline.label') }
                value={ objective.deadline }
                onChange={ handleDateChange }
                dateFormat={ getDateFormat() }
                error={ validation.deadline }
                errorMessage={ validation.deadline ? t(ERROR_MESSAGES.DEADLINE) : '' }
              />
            </Grid>
            <Grid item xs={ 4 }>
              <InputLabel>
                { t('objective.dialog.create.weight.label') }
              </InputLabel>
              <FormControl style={ { width: '100%' } }>
                <TextField
                  type={ 'number' }
                  inputProps={ {
                    min: 0,
                    max: 1000,
                    step: 0.1,
                    defaultValue: objective.weight || DEFAULT_OBJECTIVE_WEIGHT
                  } }
                  onChange={ handleOnChangeWeight }
                />
              </FormControl>
            </Grid>
            <Grid item xs={ 4 }>
              <InputLabel>
                { t('objective.dialog.create.category.label') }
              </InputLabel>
              <CategorySelect
                categories={ categories }
                category={ objective.category }
                handleOnChangeCategory={ handleOnChangeCategory }
                error={ validation.category }
                helperText={ validation.category ? t(ERROR_MESSAGES.CATEGORY) : '' }
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={ handleObjectiveSave }
          variant="contained"
          color="primary"
        >
          <div>{ t('objective.dialog.save') }</div>
        </Button>
        <Button
          onClick={ handleDialogClose }
          color="primary"
        >
          <div>{ t('objective.dialog.cancel') }</div>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObjectiveDialog;
